import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import SectionCard from '../SectionCard';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { GetInconsistenciesResponse } from '@/api/types/queries';
import { Skeleton } from '@/components/atoms/Skeleton';
import { TextWithReferences } from '@/helpers/formatReferences';
import { Inconsistency } from '@/types';

interface InconsistenciesSectionProps {
  caseId: string | null;
  inconsistenciesData: GetInconsistenciesResponse | undefined;
  inconsistenciesLoading: boolean;
  onExpandInconsistency: (inconsistency: Inconsistency) => void;
}

export const InconsistenciesSection = ({
  caseId,
  inconsistenciesData,
  inconsistenciesLoading,
  onExpandInconsistency,
}: InconsistenciesSectionProps) => {
  const navigate = useNavigate();

  const getInconsistencyLevelColor = (level: string) => {
    switch (level) {
      case 'critical':
        return 'bg-red-500';
      case 'severe':
        return 'bg-amber-500';
      case 'minor':
        return 'bg-orange-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getInconsistencyLevelText = (level: string) => {
    switch (level) {
      case 'critical':
        return 'Critical';
      case 'severe':
        return 'Severe';
      case 'minor':
        return 'Minor';
      default:
        return level;
    }
  };

  const handleViewAllInconsistencies = () => {
    navigate(`/app/chronos/case-editor/data/inconsistencies?caseId=${caseId}`);
  };

  return (
    <>
      {inconsistenciesLoading ? (
        <SectionCard title="Inconsistencies" className="h-full bg-red-50/50">
          <div className="space-y-3">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="rounded-lg border border-red-200 bg-white px-4 py-3 shadow-sm">
                {/* Header skeleton */}
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-2 w-2 rounded-full bg-gray-200" />
                    <Skeleton className="h-3 w-16 bg-gray-200" />
                  </div>
                  <Skeleton className="h-3 w-20 bg-gray-200" />
                </div>
                {/* Description skeleton */}
                <div className="space-y-1">
                  <Skeleton className="h-3 w-full bg-gray-200" />
                  <Skeleton className="h-3 w-3/4 bg-gray-200" />
                </div>
                {/* Footer skeleton */}
                <div className="mt-2 flex items-center justify-between">
                  <Skeleton className="h-3 w-24 bg-gray-200" />
                  <Skeleton className="h-3 w-16 bg-gray-200" />
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      ) : !inconsistenciesData?.inconsistencies?.length ? (
        <SectionCard title="Inconsistencies" className="h-full bg-red-50/50" headerActions={<></>}>
          <div className="flex h-full flex-col items-center justify-center text-gray-500">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mb-2 h-8 w-8 text-gray-300" />
            <span className="text-sm">No inconsistencies found</span>
          </div>
        </SectionCard>
      ) : (
        <SectionCard
          title="Inconsistencies"
          className="h-full bg-red-50/50"
          headerActions={
            <button
              onClick={() => {
                handleViewAllInconsistencies();
                trackCustomEvent(TrackingEvents.ViewAllInconsistenciesOnSummary);
              }}
              className="inline-flex items-center gap-1 text-sm text-blue-500 underline hover:text-blue-700"
            >
              View All ({inconsistenciesData?.totalCount})
              <ExternalLink className="h-3 w-3" />
            </button>
          }
        >
          <div className="space-y-3">
            {inconsistenciesData?.inconsistencies.slice(0, 3).map((inconsistency: Inconsistency) => {
              return (
                <div
                  key={inconsistency.id}
                  className="rounded-lg border border-red-200 bg-white px-4 py-3 shadow-sm transition-all hover:shadow-md"
                >
                  {/* Header with level badge */}
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex h-2 w-2 rounded-full ${getInconsistencyLevelColor(inconsistency.level)}`}
                      ></span>
                      <span className="text-xs font-medium text-gray-600">
                        {getInconsistencyLevelText(inconsistency.level)}
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="line-clamp-2 text-xs text-gray-800">
                    <TextWithReferences content={inconsistency.description} />
                  </p>

                  {/* Footer with facts count */}
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {Object.keys(inconsistency.facts).length} related facts
                    </span>
                    <button
                      className="text-xs text-blue-600 hover:text-blue-800"
                      onClick={() => {
                        onExpandInconsistency(inconsistency);
                        trackCustomEvent(TrackingEvents.ViewIndividualInconsistencyOnSummary);
                      }}
                    >
                      View details →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}
    </>
  );
};
